import { useCallback } from 'react';
import { noop } from 'lodash';
import type { FetchResult } from '@apollo/client';
import type {
  GetImageUploadQueryResult,
  UpdateProjectInput,
  UpdateProjectMutation,
} from '@frameio/graphql-client/generated';
import {
  useGetImageUploadLazyQuery,
  useUpdateProjectMutation,
} from '@frameio/graphql-client/generated';
import { useTrackUnsafe } from '../../../segment/hooks';
import type { TrackingSourceType } from '../../../segment/hooks/utils/trackingUtils';
import { uploadProjectImage } from '../utils';
import { getProjectVisibilityEnum } from '../utils/getProjectVisibilityEnum';

type UpdateProjectOpts = {
  onError?: () => void;
  onSuccess?: (
    project: NonNullable<
      NonNullable<FetchResult<UpdateProjectMutation>['data']>['updateProject']
    >['project']
  ) => void;
};

type UpdateProjectReturn = (
  details: UpdateProjectInput,
  blob: Blob | null,
  deleteAvatar: boolean,
  trackingDetails: {
    projectNameHasChanged: boolean;
    projectVisibilityHasChanged: boolean;
    trackingSource: TrackingSourceType;
  }
) => void;

const DEFAULT_OPTS: UpdateProjectOpts = {
  onError: noop,
  onSuccess: noop,
};

/**
 * The useUpdateProject hook can be used to update a project. Changing the avatar
 * requires providing an imageUploadUrl and a new blob.
 *
 * @param opts.imageUploadUrl url used for uploading the avatar
 * @param opts.onError an optional callback fired if the update fails
 * @param opts.onSuccess an optional callback fired if the update succeeds
 */
export function useUpdateProject(
  opts?: UpdateProjectOpts
): UpdateProjectReturn {
  const { onError, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [updateProject] = useUpdateProjectMutation();
  const [imageUploadQuery] = useGetImageUploadLazyQuery({
    /**
     * Avoid caching the `imageUpload` data, as a unique id is required for
     * each subsequent upload.
     */
    fetchPolicy: 'network-only',
  });

  const track = useTrackUnsafe();

  return useCallback(
    /**
     * @param details the UpdateProjectInput values including id, name and image
     * @param blob Blob instance for the avatar
     * @param deleteAvatar
     * @param trackingDetails data passed through to event tracking
     */
    async (details, blob, deleteAvatar, trackingDetails) => {
      const input = {
        ...details,
      };
      const blobUrl = blob ? URL.createObjectURL(blob) : null;
      let imageUploadResult: GetImageUploadQueryResult;
      let imageUploadUrl: string | null = null;

      if (blob && !deleteAvatar) {
        imageUploadResult = await imageUploadQuery({
          variables: {
            mimeType: blob.type,
            resourceId: input.id,
            resourceType: 'PROJECT',
          },
        });

        if (imageUploadResult?.data?.imageUpload?.url) {
          imageUploadUrl = imageUploadResult.data.imageUpload.url;
          input.image = {
            id: imageUploadResult.data.imageUpload.id,
            type: blob.type,
          };
        }
      } else if (deleteAvatar) {
        input.image = null;
      }

      const handleResolve = (result: FetchResult<UpdateProjectMutation>) => {
        const updatedProject = result?.data?.updateProject?.project;

        if (updatedProject) {
          track(
            'project-updated',
            {
              new_project_visibility: getProjectVisibilityEnum(
                updatedProject.private
              ),
              project_name_has_changed: trackingDetails.projectNameHasChanged,
              project_visibility_has_changed:
                trackingDetails.projectVisibilityHasChanged,
              source: trackingDetails.trackingSource,
            },
            { logToDatadog: true }
          );

          onSuccess?.(updatedProject);
        } else {
          onError?.();
        }
      };

      const handleReject = () => {
        onError?.();
      };

      uploadProjectImage(imageUploadUrl, blob, () => {
        updateProject({
          optimisticResponse: {
            __typename: 'Mutation',
            updateProject: {
              __typename: 'UpdateProjectPayload',
              project: {
                __typename: 'Project',
                id: input.id,
                description: input.description ?? '',
                ignoreArchive: input.ignoreArchive ?? false,
                mountedStorageDriveLetter:
                  input.mountedStorageDriveLetter ?? null,
                mountedStorageStatus: input.mountedStorageStatus ?? 'LOCKED',
                images: {
                  __typename: 'Images',
                  image128: blobUrl,
                  image256: blobUrl,
                  image32: blobUrl,
                  image64: blobUrl,
                  imageBase: blobUrl,
                  original: blobUrl,
                },
                imageUrl: blobUrl,
                name: input.name ?? '',
                private: input.private ?? false,
              },
            },
          },
          variables: {
            input,
          },
        }).then(handleResolve, handleReject);
      });
    },
    [imageUploadQuery, onSuccess, track, onError, updateProject]
  );
}
